import * as _ from 'lodash';
import { cloneElement, useMemo } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import type { FunctionComponent, ReactElement } from 'react';

import Locator from '../../../../shared/Locator';
import { isDependencyCacheValid } from '../../../helpers/revisions';
import ProjectGroupModule from '../../../redux/project_grouping';
import { getProjectBaseURLFromParams } from '../../../utils';
import { Link } from '../../generic';
import List from '../../generic/List';
import type { ProjectGroupRelease, Revision } from '../../../types';

type Props = {
  release: ProjectGroupRelease;
  isReleaseLoading: boolean;
};

const ProjectGroupDependencyValidityContainer: FunctionComponent<Props> = ({
  release,
  isReleaseLoading,
  children,
  ...props
}) => {
  const content = useMemo(() => {
    if (!release || isReleaseLoading) {
      return null;
    }

    const invalidProjects = release.projects.filter((project) =>
      !isDependencyCacheValid(project.revision as Revision),
    );

    const isAllDependencyCacheValid = _.isEmpty(invalidProjects);

    if (isAllDependencyCacheValid) {
      return cloneElement(children as ReactElement, props);
    }

    const minimalInvalidProjects = invalidProjects.map((project) => {
      const locator = new Locator(project.revisionId);
      const revisionLink = getProjectBaseURLFromParams({
        project_id: project.projectId,
        reference_type: 'branch',
        reference: project.branch,
        revision_id: locator.revision.value as string,
      });
      return {
        projectLocator: project.projectId,
        projectTitle: project.project.title,
        revisionLink,
      };
    });

    return (
      <div className="container container-responsive">
        <div className="margin-top">
          <div className="alert alert-warning">
            Please wait while FOSSA determines the dependencies for all revisions, this step can take a few minutes to
            complete...
          </div>
          <div>
            <List>
              <List.Header>
                <List.HeaderText>
                  The following projects are still being analyzed:
                </List.HeaderText>
              </List.Header>
              {minimalInvalidProjects.map((project) => (
                <List.Row key={project.projectLocator}>
                  <Link to={project.revisionLink}>
                    {project.projectTitle || project.projectLocator}
                  </Link>
                </List.Row>
              ))}
            </List>
          </div>
        </div>
      </div>
    );
  }, [children, props, release, isReleaseLoading]);

  return content;
};

const mapStateToProps = createStructuredSelector({
  release: ProjectGroupModule.selectors.getRelease,
  isReleaseLoading: ProjectGroupModule.selectors.getReleaseIsLoading,
});

export default connect(
  mapStateToProps,
)(ProjectGroupDependencyValidityContainer);
